import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Browser from '@wetransfer/concorde-browser';

import I18n from '@module/i18n';
import get from 'lodash/get';

import Actions from '@app/action';

import Icon from '@component/icon';
import PopTip from '@component/info/poptip';
import Scrollable from '@component/scrollable';

import { isDefaultRecipientInUploadLocked } from '@helper/experiments';

import Fields from './fields';
import Files from './files';
import Options from './options';
import LockedRecipient from './locked-recipient';

export class Form extends React.Component {
  state = {
    showOptions: false,
    showDefaultRecipientMobileInfo: false,
    transferStarted: false,
  };

  constructor(props) {
    super(props);

    this.hideOptions = this.hideOptions.bind(this);
    this.hideOptionsOnScroll = this.hideOptionsOnScroll.bind(this);
    this.toggleOptions = this.toggleOptions.bind(this);
    this.startTransfer = this.startTransfer.bind(this);
    this.handleLockedRecipientClick = this.handleLockedRecipientClick.bind(
      this
    );
    this.handleLockedRecipientInfoClick = this.handleLockedRecipientInfoClick.bind(
      this
    );
  }

  UNSAFE_componentWillMount() {
    if (this.props.transfer.state === 'downloadable') {
      this.props.create();
    }
  }

  toggleOptions() {
    if (this.state.showOptions) {
      return this.hideOptions();
    }
    this.setState({ showOptions: true });
  }

  hideOptions() {
    if (!this.state.showOptions) {
      return;
    }
    // Find the options node
    if (!this.optionsNode) {
      return;
    }

    // Calculate new scroll position
    const newTop =
      this.getScrollPosition() -
      this.optionsNode.getWrappedInstance().node.offsetHeight;
    this.scrollTo(newTop, { animated: true, duration: 300 });
  }

  hideOptionsOnScroll(e) {
    if (!this.optionsNode) {
      return;
    }

    if (!this.state.showOptions) {
      return;
    }

    if (!this.optionsNode) {
      return;
    }

    // If the element is (close to) out of view, hide it again.
    if (
      e.scrollTop + e.clientHeight <=
      this.optionsNode.getWrappedInstance().node.offsetTop + 5
    ) {
      this.props.clearTransferNotification();
      this.setState({ showOptions: false });
    }
  }

  isDisabled() {
    const { session, transfer } = this.props;
    const { transferStarted } = this.state;
    return !session.tandc || transfer.hasStarted || transferStarted;
  }

  // Start the actual transfer, if it passes some checks.
  startTransfer() {
    const { clearTransferNotification, init } = this.props;
    if (this.isDisabled()) {
      return false;
    }

    this.setState({ transferStarted: true }, () => {
      clearTransferNotification();
      init();
    });
  }

  // Proxy scrollTo function
  scrollTo(...args) {
    if (!this.scrollable) {
      return false;
    }
    this.scrollable.scrollTo(...args);
  }

  getScrollPosition() {
    return this.scrollable.getScrollPosition();
  }

  getScrollHeight() {
    return this.scrollable.getScrollHeight();
  }

  componentDidUpdate(prevProps, prevState) {
    // Options visible? Show them in scroll view
    if (
      prevState.showOptions !== this.state.showOptions &&
      this.state.showOptions === true
    ) {
      this.scrollTo('bottom', {
        animated: true,
        duration: 400,
        blockEvents: true,
      });
    }

    // Transfer type switched? We're working in the options
    if (prevProps.transferType !== this.props.transfer.type) {
      this.scrollTo('bottom');
    }

    if (
      get(this, 'props.notification.transfer.text', false) &&
      this.state.transferStarted
    ) {
      this.setState({ transferStarted: false });
    }
  }

  renderFooter() {
    const { transfer } = this.props;
    const optionsColor = this.state.showOptions ? '#0073e5' : '#409FFF';
    let isTransferDisabled = this.isDisabled();
    let buttonLabel = I18n.t('transfer.buttons.transfer');
    if (transfer.isForward) {
      buttonLabel = I18n.t('transfer.buttons.forward');
    }

    // If the transfer isn't valid, e.g. transfer is processing,
    // we won't activate the button
    try {
      transfer.validate();
    } catch (e) {
      isTransferDisabled = true;
    }

    const transferButtonBase = 'transfer__button';
    const buttonClassName = classnames(transferButtonBase, {
      [`${transferButtonBase}--inactive`]: isTransferDisabled,
    });

    const showPoptip = false;
    const popTipCopy = React.createElement('span', null, null);

    return (
      <div className="transfer__footer">
        {Browser.isMobile ? null : (
          <PopTip
            visible={showPoptip}
            autoHide={true}
            ref={(node) => (this.popTip = node)}
            trigger="manual"
            className="contact-item__add-tooltip"
            content={popTipCopy}
            anchor="bottom-right"
            offset={15}
          >
            <Icon
              type="TransferOptions"
              onClick={this.toggleOptions}
              color={optionsColor}
            />
          </PopTip>
        )}
        <button
          type="button"
          disabled={isTransferDisabled}
          onClick={this.startTransfer}
          className={buttonClassName}
        >
          {buttonLabel}
        </button>
      </div>
    );
  }

  handleLockedRecipientInfoClick() {
    if (Browser.isMobile) {
      this.setState({ showDefaultRecipientMobileInfo: false });
    }
  }

  handleLockedRecipientClick() {
    if (Browser.isMobile) {
      this.setState({ showDefaultRecipientMobileInfo: true });
    } else {
      this.props.pushTransferNotification({
        text: 'LockedRecipient',
      });
    }
  }

  render() {
    const {
      transfer,
      channel,
      session: {
        region: { country },
      },
      user,
      account,
    } = this.props;

    const isRecipientLocked = isDefaultRecipientInUploadLocked(
      country,
      channel.defaultRecipient,
      user.loggedIn,
      channel.name,
      account.subdomain_name
    );

    return (
      <div
        ref={(node) => (this.node = node)}
        className={`transfer__window uploader uploader--form uploader--type-${
          transfer.type
        }`}
      >
        <Scrollable
          ref={(node) => (this.scrollable = node)}
          className="transfer__contents"
          onScroll={this.hideOptionsOnScroll}
        >
          <Files
            ref={(node) => (this.files = node)}
            transfer={transfer}
            fileCount={transfer.files.length}
            panel={this.node}
            panelComponent={this}
            onClick={this.hideOptions}
          />
          {isRecipientLocked && (
            <LockedRecipient
              recipient={channel.defaultRecipient}
              showMobileInfo={
                Browser.isMobile && this.state.showDefaultRecipientMobileInfo
              }
              onClick={this.handleLockedRecipientClick}
              onInfoClick={this.handleLockedRecipientInfoClick}
            />
          )}
          <Fields
            ref={(node) => (this.fields = node)}
            onInteraction={this.hideOptions}
            transfer={transfer}
            type={transfer.type}
            fileCount={transfer.files.length}
            recipientCount={transfer.recipients.length}
            panel={this}
            isRecipientLocked={isRecipientLocked}
          />
          {Browser.isMobile ? null : (
            <Options
              ref={(node) => (this.optionsNode = node)}
              type={transfer.type}
              password={transfer.password}
              expiry={transfer.expiry}
              isVisible={this.state.showOptions}
              isRecipientLocked={isRecipientLocked}
            />
          )}
        </Scrollable>
        {this.renderFooter()}
      </div>
    );
  }
}

Form.propTypes = {
  clearTransferNotification: PropTypes.func.isRequired,
  pushTransferNotification: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  init: PropTypes.func.isRequired,
  transfer: PropTypes.object.isRequired,
  scrollTo: PropTypes.func,
  session: PropTypes.object.isRequired,
  notification: PropTypes.object,
  channel: PropTypes.object,
  user: PropTypes.object,
  transferType: PropTypes.string,
  account: PropTypes.object,
};

const mapDispatchToProps = (dispatch) => ({
  clearTransferNotification: () =>
    dispatch(Actions.Notification.clearTransfer()),
  pushTransferNotification: (options) =>
    dispatch(Actions.Notification.pushTransfer(options)),
  create: () => dispatch(Actions.Transfer.create()),
  init: () => dispatch(Actions.Transfer.init()),
});

export default connect(
  null,
  mapDispatchToProps
)(Form);
